/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { Search, Filter, XCircle, ChevronDown, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";
import ResetPasswordModal from "../../../components/ResetPasswordModal";
import {
  SUBCITIES,
  VETTING_STAGES,
  VETTING_STAGE_LABELS,
  workTypeOptions,
} from "../../../utils/constants";
import DeleteConfirmationModal from "../../../components/caregivers/DeleteConfirmationModal";
import UploadCaregiversModal from "../../../components/caregivers/UploadCaregiversModal";
import VettingHistoryModal from "../../../components/caregivers/VettingHistoryModal";
import CaregiverTable from "../../../components/caregivers/CaregiverTable";
import BankDetailsModal from "../../../components/caregivers/BankDetailsModal";

const CareGiverList = () => {
  const navigate = useNavigate();
  const {
    careGivers,
    careGiverPagination,
    getAllCareGivers,
    changeAvailablity,
    changeBookingAvailablity,
    toggleCareGiverStatus,
    deleteCareGiverById,
    getCaregiverVettingHistory,
    resetCareGiverPassword,
    serviceCategories,
  } = useContext(AdminContext);

  // Filters
  const [nameSearch, setNameSearch] = useState("");
  const [phoneSearch, setPhoneSearch] = useState("");
  const [nameInput, setNameInput] = useState(""); // Separate input state
  const [phoneInput, setPhoneInput] = useState(""); // Separate input state
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [vettingFilter, setVettingFilter] = useState("All");
  const [subcityFilter, setSubcityFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Dropdown states
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showVettingDropdown, setShowVettingDropdown] = useState(false);
  const [showSubcityDropdown, setShowSubcityDropdown] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // UI State
  const [expandedRow, setExpandedRow] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);

  // Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caregiverToDelete, setCaregiverToDelete] = useState(null);
  const [caregiverToDeleteName, setCaregiverToDeleteName] = useState("");

  const [showUploadModal, setShowUploadModal] = useState(false);

  const [showVettingHistory, setShowVettingHistory] = useState(false);
  const [vettingHistory, setVettingHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [caregiverToReset, setCaregiverToReset] = useState(null);
  const [caregiverResetData, setCaregiverResetData] = useState(null);

  const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);
  const [selectedCaregiverForBank, setSelectedCaregiverForBank] =
    useState(null);

  // Category options - use dynamic categories from context
  const categories = [
    { id: "All", title: "All Categories" },
    ...serviceCategories.map((cat) => ({
      id: cat.id,
      title: cat.label,
    })),
  ];

  // Format date function (replaces moment)
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";

      const options = {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };
      return date.toLocaleDateString("en-US", options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  // Fetch caregivers with filters
  const fetchCaregivers = async (page = currentPage) => {
    setIsLoading(true);
    try {
      await getAllCareGivers({
        page,
        limit: itemsPerPage,
        name: nameSearch,
        phone: phoneSearch,
        category: categoryFilter === "All" ? "" : categoryFilter,
        vettingStatus: vettingFilter === "All" ? "" : vettingFilter,
        subcity: subcityFilter,
      });
    } catch (error) {
      toast.error("Failed to fetch caregivers");
    } finally {
      setIsLoading(false);
    }
  };

  // Search handlers
  const handleSearch = () => {
    setNameSearch(nameInput);
    setPhoneSearch(phoneInput);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCaregivers(currentPage);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [
    currentPage,
    itemsPerPage,
    nameSearch,
    phoneSearch,
    categoryFilter,
    vettingFilter,
    subcityFilter,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [nameSearch, phoneSearch, categoryFilter, vettingFilter, subcityFilter]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".relative")) {
        setShowCategoryDropdown(false);
        setShowVettingDropdown(false);
        setShowSubcityDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Map API data to component format
  // Defensive check: use empty array if careGivers is undefined or null
  const mappedCaregivers = (careGivers || []).map((c) => ({
    id: c.id,
    name: c.name,
    avatar: c.image || null,
    phone: c.phoneNumber,
    email: c.email,
    category:
      c.serviceCategory?.label || c.serviceCategoryId || c.category || "",
    fees: c.fees ? Number(c.fees) : 0,
    rating: Number(c.rating) || 0,
    vettingStatus:
      c.vettingStatus || c.vetting_status || "Application_Submitted",
    isAvailable: c.isAvailable ?? c.available ?? true,
    isBooked: c.isBooked ?? c.is_booked ?? false,
    isActive: c.status === "active",
    experience: `${c.experience || 0} Years`,
    nationality: c.nationality || "Not Specified",
    languages: Array.isArray(c.languages)
      ? c.languages
      : typeof c.languages === "string"
        ? c.languages.split(", ")
        : ["Amharic"],
    education: c.education || "Not Specified",
    about: c.about || "Not Specified",
    hasCourse: c.hasCourse ?? false,
    courseDetails: c.courseDetails || "",
    courseDetails_amh: c.courseDetails_amh || "",
    hasReferee: c.hasReferee ?? false,
    refereeName: c.refereeName || "",
    refereePhone: c.refereePhone || "",
    workPreferences: (() => {
      const pref = c.workTimePreference || c.date_time_preference_to_work;
      if (!pref) return "Full-Time";
      const found = workTypeOptions.find((o) => o.value === pref);
      return found
        ? found.label
        : pref.replace("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());
    })(),
    createdAt: c.createdAt,
    onboardedAt: c.onboardedAt,
    subcity: c.subcity || "",
    addressLine1: c.addressLine1 || "",
    addressLine2: c.addressLine2 || "",
  }));

  const handleToggleStatus = async (id, field) => {
    try {
      if (field === "isAvailable") {
        await changeAvailablity(id);
      } else if (field === "isBooked") {
        await changeBookingAvailablity(id);
      } else if (field === "isActive") {
        await toggleCareGiverStatus(id);
      }
      fetchCaregivers();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteClick = (id, name) => {
    setCaregiverToDelete(id);
    setCaregiverToDeleteName(name);
    setShowDeleteModal(true);
    setActiveMenu(null);
  };

  const confirmDelete = async () => {
    if (caregiverToDelete) {
      try {
        const result = await deleteCareGiverById(caregiverToDelete);
        if (result.success) {
          toast.success(result.message || "Caregiver deleted successfully");
          setShowDeleteModal(false);
          setCaregiverToDelete(null);
          setCaregiverToDeleteName("");
          fetchCaregivers();
        } else {
          toast.error(result.message || "Failed to delete caregiver");
        }
      } catch (error) {
        toast.error("Failed to delete caregiver");
      }
    }
  };

  // Handle reset password for caregiver
  const handleResetPasswordClick = (caregiver) => {
    setCaregiverToReset(caregiver.id);
    setCaregiverResetData({
      name: caregiver.name,
      email: caregiver.email,
      phoneNumber: caregiver.phone,
      image: caregiver.avatar,
    });
    setShowResetPasswordModal(true);
    setActiveMenu(null);
  };

  const handleResetCaregiverPassword = async () => {
    if (!caregiverToReset) return;

    try {
      await resetCareGiverPassword(caregiverToReset);
      setShowResetPasswordModal(false);
    } catch (error) {
      console.error("Error resetting caregiver password:", error);
    }
  };

  // Handle viewing vetting history
  const handleViewVettingHistory = async (caregiverId) => {
    setLoadingHistory(true);
    try {
      const history = await getCaregiverVettingHistory(caregiverId);
      setVettingHistory(history || []);
      setShowVettingHistory(true);
    } catch (error) {
      toast.error("Failed to fetch vetting history");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleViewBankDetails = (caregiverId) => {
    setSelectedCaregiverForBank(caregiverId);
    setShowBankDetailsModal(true);
  };

  // Upload Handlers
  const handleUploadClick = () => {
    setShowUploadModal(true);
  };

  const handleUploadSubmit = (data) => {
    setShowUploadModal(false);
    toast.success("Caregivers uploaded successfully!");
  };

  // Reset all filters
  const resetFilters = () => {
    setNameSearch("");
    setPhoneSearch("");
    setNameInput("");
    setPhoneInput("");
    setCategoryFilter("All");
    setVettingFilter("All");
    setSubcityFilter("");
    setCurrentPage(1);
  };

  // Format category for display
  const formatCategory = (category) => {
    return categories.find((c) => c.id === category)?.title || category;
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            Caregivers
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm sm:text-base">
            Manage caregiver profiles, availability, and vetting.
            {careGiverPagination && (
              <span className="ml-2 font-semibold text-primary">
                ({careGiverPagination.total} total)
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate("/caregivers/add")}
            className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-bold transition-colors shadow-lg shadow-primary/20 dark:shadow-none"
          >
            <User size={18} /> Add Caregiver
          </button>
        </div>
      </div>

      {/* Filters Toggle Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition-all ${
            showFilters
              ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary ring-2 ring-primary/20"
              : "bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700"
          }`}
        >
          <Filter size={18} />
          <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
        </button>
      </div>

      {/* Filters Section */}
      <div
        className={`bg-card dark:bg-dark-surface p-4 rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border mb-6 transition-all duration-300 ${
          showFilters ? "block" : "hidden"
        }`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4 items-end">
          {/* Name Search */}
          <div className="relative">
            <label className="block text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1 mb-1.5">
              Search by Name
            </label>
            <Search
              className="absolute left-4 bottom-3 text-primary"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by name..."
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 font-medium transition-all"
            />
          </div>

          {/* Phone Search */}
          <div className="relative">
            <label className="block text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1 mb-1.5">
              Search by Phone
            </label>
            <Search
              className="absolute left-4 bottom-3 text-primary"
              size={18}
            />
            <input
              type="text"
              placeholder="+251 or 09... (digits only)"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 font-medium transition-all"
            />
          </div>

          {/* Search and Reset Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all bg-primary hover:bg-primary/90 text-white"
            >
              <Search size={18} />
              <span>Search</span>
            </button>
            <button
              onClick={resetFilters}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all bg-gray-50 dark:bg-gray-900 text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <XCircle size={18} />
              <span>Reset</span>
            </button>
          </div>
        </div>

        {/* Category, Vetting, and Subcity Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Category
            </label>
            <div className="relative">
              <button
                onClick={() => {
                  setShowCategoryDropdown(!showCategoryDropdown);
                  setShowVettingDropdown(false);
                  setShowSubcityDropdown(false);
                }}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border-none text-sm font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>
                  {categories.find((c) => c.id === categoryFilter)?.title ||
                    "All Categories"}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-primary transition-transform ${
                    showCategoryDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showCategoryDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setCategoryFilter(category.id);
                        setShowCategoryDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        categoryFilter === category.id
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {category.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Vetting Status
            </label>
            <div className="relative">
              <button
                onClick={() => {
                  setShowVettingDropdown(!showVettingDropdown);
                  setShowCategoryDropdown(false);
                  setShowSubcityDropdown(false);
                }}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border-none text-sm font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>
                  {vettingFilter === "All"
                    ? "All Statuses"
                    : VETTING_STAGE_LABELS[vettingFilter] || vettingFilter}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-primary transition-transform ${
                    showVettingDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showVettingDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                  {VETTING_STAGES.map((stage) => (
                    <button
                      key={stage}
                      onClick={() => {
                        setVettingFilter(stage);
                        setShowVettingDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        vettingFilter === stage
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {stage === "All"
                        ? "All Statuses"
                        : VETTING_STAGE_LABELS[stage] || stage}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Subcity
            </label>
            <div className="relative">
              <button
                onClick={() => {
                  setShowSubcityDropdown(!showSubcityDropdown);
                  setShowCategoryDropdown(false);
                  setShowVettingDropdown(false);
                }}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border-none text-sm font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>{subcityFilter || "All Subcities"}</span>
                <ChevronDown
                  size={14}
                  className={`text-primary transition-transform ${
                    showSubcityDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showSubcityDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                  <button
                    onClick={() => {
                      setSubcityFilter("");
                      setShowSubcityDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      subcityFilter === ""
                        ? "bg-primary text-white dark:bg-primary"
                        : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                    }`}
                  >
                    All Subcities
                  </button>
                  {SUBCITIES.map((subcity) => (
                    <button
                      key={subcity}
                      onClick={() => {
                        setSubcityFilter(subcity);
                        setShowSubcityDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        subcityFilter === subcity
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {subcity}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table Component */}
      <CaregiverTable
        caregivers={mappedCaregivers}
        pagination={careGiverPagination}
        isLoading={isLoading}
        showVettingColumn={true}
        showWorkingColumn={false}
        showUploadButton={true}
        onUploadClick={handleUploadClick}
        onAddClick={() => navigate("/add-caregiver")}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onToggleStatus={handleToggleStatus}
        onDeleteClick={handleDeleteClick}
        onResetPasswordClick={handleResetPasswordClick}
        onViewVettingHistory={handleViewVettingHistory}
        onViewBankDetails={handleViewBankDetails}
        onDownloadCV={null}
        expandedRow={expandedRow}
        onToggleExpand={setExpandedRow}
        activeMenu={activeMenu}
        onSetActiveMenu={setActiveMenu}
        formatCategory={formatCategory}
        formatDate={formatDate}
      />

      {/* Modals */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCaregiverToDelete(null);
          setCaregiverToDeleteName("");
        }}
        onConfirm={confirmDelete}
        caregiverName={caregiverToDeleteName}
      />

      <UploadCaregiversModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        categories={categories}
        onUpload={handleUploadSubmit}
      />

      <VettingHistoryModal
        isOpen={showVettingHistory}
        onClose={() => setShowVettingHistory(false)}
        vettingHistory={vettingHistory}
        loadingHistory={loadingHistory}
      />

      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        onConfirm={handleResetCaregiverPassword}
        userData={caregiverResetData}
      />

      <BankDetailsModal
        isOpen={showBankDetailsModal}
        onClose={() => {
          setShowBankDetailsModal(false);
          setSelectedCaregiverForBank(null);
          fetchCaregivers();
        }}
        caregiverId={selectedCaregiverForBank}
      />
    </div>
  );
};

export default CareGiverList;
